/**
 * Updates the recently viewed products in localStorage.
 *
 * CONSENT-GATED. Cookiebot's scanner counts localStorage entries as cookies and
 * classified `viewedProducts` as Preferences (CH + UAE groups, 2026-08). Writing
 * it before the visitor has accepted Preferences is what turned the CH scan of
 * 20.08.2026 into "Prior consent fully enabled: no". So a product is only
 * recorded once Preferences consent exists, and the list is wiped when that
 * consent is absent (see the cleanup listener in snippets/cookiebot.liquid,
 * which owns the storage keys gated this way).
 *
 * On a store without Cookiebot (no domain group in snippets/cookiebot-cbid.liquid,
 * or the theme editor) `window.Cookiebot` is undefined and nothing is gated.
 */
export class RecentlyViewed {
  /** @static @constant {string} The key used to store the viewed products in session storage */
  static #STORAGE_KEY = 'viewedProducts';
  /** @static @constant {number} The maximum number of products to store.
   *  Kept comfortably above the section's "Max products" display setting (which tops out
   *  at 12) plus the current product, so the section always has enough history to draw from. */
  static #MAX_PRODUCTS = 20;

  /**
   * Adds a product to the recently viewed products list.
   * @param {string} productId - The ID of the product to add.
   */
  static addProduct(productId) {
    if (!RecentlyViewed.#hasPreferencesConsent()) {
      // Not consented (yet). If the visitor accepts on this very page, record it
      // then. CookiebotOnConsentReady fires on every consent change, but also on
      // page load for a returning visitor who declined earlier, so the listener
      // has to survive that first, still-declined firing and only detach once a
      // write actually happened ({ once: true } would be eaten by the load event).
      const onConsentReady = () => {
        if (!RecentlyViewed.#hasPreferencesConsent()) return;
        window.removeEventListener('CookiebotOnConsentReady', onConsentReady);
        RecentlyViewed.addProduct(productId);
      };
      window.addEventListener('CookiebotOnConsentReady', onConsentReady);
      return;
    }

    let viewedProducts = this.getProducts();

    viewedProducts = viewedProducts.filter((/** @type {string} */ id) => id !== productId);
    viewedProducts.unshift(productId);
    viewedProducts = viewedProducts.slice(0, this.#MAX_PRODUCTS);

    localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(viewedProducts));
  }

  static clearProducts() {
    localStorage.removeItem(this.#STORAGE_KEY);
  }

  /**
   * Retrieves the list of recently viewed products from session storage.
   * @returns {string[]} The list of viewed products.
   */
  static getProducts() {
    return JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]');
  }

  /**
   * Whether the visitor has accepted Cookiebot's Preferences category. True when
   * no consent manager is present on the page, because then there is nothing to
   * gate on (see the class doc block).
   * @returns {boolean}
   */
  static #hasPreferencesConsent() {
    const cookiebot = window.Cookiebot;
    if (!cookiebot) return true;
    return !!(cookiebot.consent && cookiebot.consent.preferences);
  }
}
